// src/components/workout/ExerciseProgressChart.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, parseISO, subMonths } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { useToast } from "../ui/use-toast";

const ExerciseProgressChart = ({ userId, unitSystem = 'imperial' }) => {
  const [exercises, setExercises] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState('');
  const [timeRange, setTimeRange] = useState('3m'); // 1m, 3m, 6m, 1y, all
  const [progressData, setProgressData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Fetch available exercises
  useEffect(() => {
    const fetchExercises = async () => {
      if (!userId) return;
    
      try {
        // First, get all workouts for this user
        const { data: workouts, error: workoutsError } = await supabase
          .from('workouts')
          .select('id')
          .eq('user_id', userId);
        
        if (workoutsError) throw workoutsError;
        
        if (!workouts || workouts.length === 0) {
          setExercises([]);
          return;
        }
        
        // Get workout IDs
        const workoutIds = workouts.map(w => w.id);
        
        // Now fetch exercises for these workouts
        const { data, error } = await supabase
          .from('workout_exercises')
          .select('exercise_name')
          .in('workout_id', workoutIds)
          .order('exercise_name');
    
        if (error) throw error;
    
        // Get unique exercise names
        const uniqueExercises = [...new Set(data.map(item => item.exercise_name))];
        setExercises(uniqueExercises);
    
        // Set default selected exercise
        if (uniqueExercises.length > 0 && !selectedExercise) {
          setSelectedExercise(uniqueExercises[0]);
        }
      } catch (error) {
        console.error('Error fetching exercises:', error);
        toast({
          title: 'Error',
          description: 'Failed to load exercises',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchExercises();
  }, [userId, toast, selectedExercise]);

  // Fetch progress data when exercise or time range changes
  useEffect(() => {
    const fetchProgressData = async () => {
      if (!userId || !selectedExercise) return;
    
      setLoading(true);
      try {
        // Calculate date range
        let startDate = null;
        const now = new Date();
        
        switch (timeRange) {
          case '1m':
            startDate = subMonths(now, 1);
            break;
          case '3m':
            startDate = subMonths(now, 3);
            break;
          case '6m':
            startDate = subMonths(now, 6);
            break;
          case '1y':
            startDate = subMonths(now, 12);
            break;
          default:
            // 'all' - no date filtering
            break;
        }
    
        // First, get all workouts for this user
        let workoutsQuery = supabase
          .from('workouts')
          .select('id, completed_at')
          .eq('user_id', userId)
          .order('completed_at', { ascending: true });
        
        // Apply date filter if needed
        if (startDate) {
          workoutsQuery = workoutsQuery.gte('completed_at', startDate.toISOString());
        }
        
        const { data: workouts, error: workoutsError } = await workoutsQuery;
        
        if (workoutsError) throw workoutsError;
        
        if (!workouts || workouts.length === 0) {
          setProgressData([]);
          return;
        }
        
        // Get workout IDs and create a date lookup
        const workoutIds = workouts.map(w => w.id);
        const workoutDates = Object.fromEntries(workouts.map(w => [w.id, w.completed_at]));
        
        // Now fetch exercise data
        const { data, error } = await supabase
          .from('workout_exercises')
          .select(`
            id,
            exercise_name,
            weight,
            workout_id,
            workout_sets (reps_completed, target_reps)
          `)
          .eq('exercise_name', selectedExercise)
          .in('workout_id', workoutIds);
    
        if (error) throw error;
    
        // Process data for the chart
        const formattedData = data.map(record => {
          // Get workout date from our lookup
          const workoutDate = workoutDates[record.workout_id];
          
          // Calculate success rate for the sets
          const sets = record.workout_sets || [];
          const successfulSets = sets.filter(set => 
            set.reps_completed >= set.target_reps
          ).length;
          const successRate = sets.length > 0 ? (successfulSets / sets.length) * 100 : 0;
    
          return {
            date: format(parseISO(workoutDate), 'MMM d, yyyy'),
            weight: record.weight,
            successRate: Math.round(successRate)
          };
        });
    
        setProgressData(formattedData);
      } catch (error) {
        console.error('Error fetching progress data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load progress data',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProgressData();
  }, [userId, selectedExercise, timeRange, toast]);

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    
    return (
      <div className="bg-background border rounded-md p-3 shadow-lg">
        <p className="font-medium">{label}</p>
        <p>Weight: {payload[0].value} {unitSystem === 'imperial' ? 'lbs' : 'kg'}</p>
        <p>Success Rate: {payload[1].value}%</p>
      </div>
    );
  };

  if (loading && !progressData.length) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center h-60">
          <p className="text-muted-foreground">Loading exercise data...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      {/* Completely redesigned header section for better mobile layout */}
      <CardHeader className="pb-2">
        <div className="mb-4">
          <CardTitle className="text-2xl font-bold">Exercise Progress</CardTitle>
          <CardDescription className="text-muted-foreground mt-1">
            Track your lifting progress over time
          </CardDescription>
        </div>
        
        {/* Controls in their own section, properly sized and spaced */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
          <Select value={selectedExercise} onValueChange={setSelectedExercise}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select exercise" />
            </SelectTrigger>
            <SelectContent>
              {exercises.map(exercise => (
                <SelectItem key={exercise} value={exercise}>{exercise}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1m">1 Month</SelectItem>
              <SelectItem value="3m">3 Months</SelectItem>
              <SelectItem value="6m">6 Months</SelectItem>
              <SelectItem value="1y">1 Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent>
        {progressData.length > 0 ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={progressData} margin={{ top: 5, right: 20, bottom: 20, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    // Shorter date format for small screens
                    return value.split(',')[0];
                  }}
                />
                <YAxis 
                  yAxisId="left" 
                  orientation="left" 
                  label={{ 
                    value: `Weight (${unitSystem === 'imperial' ? 'lbs' : 'kg'})`, 
                    angle: -90, 
                    position: 'insideLeft' 
                  }} 
                />
                <YAxis 
                  yAxisId="right" 
                  orientation="right" 
                  label={{ 
                    value: 'Success Rate (%)', 
                    angle: 90, 
                    position: 'insideRight' 
                  }}
                  domain={[0, 100]}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line 
                  yAxisId="left" 
                  type="monotone" 
                  dataKey="weight" 
                  stroke="#8884d8" 
                  name={`Weight (${unitSystem === 'imperial' ? 'lbs' : 'kg'})`}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line 
                  yAxisId="right" 
                  type="monotone" 
                  dataKey="successRate" 
                  stroke="#82ca9d" 
                  name="Success Rate (%)"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-60 text-center">
            <p className="text-muted-foreground mb-2">
              {exercises.length > 0 
                ? `No data available for ${selectedExercise}${timeRange !== 'all' ? ' in the selected time range' : ''}.`
                : "No exercise data available yet."}
            </p>
            {exercises.length === 0 && (
              <Button 
                variant="outline" 
                onClick={() => window.location.href = '/workouts'}
              >
                Log Your First Workout
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExerciseProgressChart;